import type { UseQueryHookResult } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getScorLanguage } from 'i18n/scorLocale';
import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import getFormDataFromObject from 'utils/getFormDataFromObject';

export const getScorFormData = <Data extends object>(
  data: Data & { fields?: Record<string, string | number | boolean> },
  documents: File[],
  uploadField = 'documents[]'
): FormData => {
  const { fields = {}, ...formDataSource } = data;
  const normalizedFormDataSource = Object.fromEntries(
    Object.entries(formDataSource)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [
        key,
        typeof value === 'boolean' ? (value ? '1' : '0') : value,
      ])
  );
  const formData = getFormDataFromObject(normalizedFormDataSource);
  Object.entries(fields).forEach(([code, value]) => {
    formData.append(
      `fields[${code}]`,
      typeof value === 'boolean' ? (value ? '1' : '0') : String(value)
    );
  });
  documents.forEach((file) => formData.append(uploadField, file));

  return formData;
};

export const scorApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchScorSharedPreview: builder.query<ApiScorResponse<ApiScorSharedPreview>, { token: string; language: string }>({
      query: ({ language, token }) => ({
        url: '/v3/scor/shared/preview',
        timeout: 20000,
method: 'GET',
sendToken: false,
        language,
params: { token },
      }),
    }),
    markScorFaqViewed: builder.mutation<ApiScorResponse<{ counted?: number }>, { id: number }>({
      query: ({ id }) => ({
        url: '/v3/scor/scor/faq-view',
        method: 'POST',
        sendToken: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ id: String(id) }).toString(),
      }),
    }),
    fetchScorShowcase: builder.query<
      ApiScorResponse<ApiScorShowcaseResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/showcase',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorShowcase'],
    }),
    fetchScorPricing: builder.query<ApiScorResponse<ApiScorPricing>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/pricing',
        method: 'GET',
        sendToken: true,
      }),
    }),
    fetchScorFaq: builder.query<
      ApiScorResponse<ApiScorFaqResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/faq',
        method: 'GET',
        sendToken: true,
      }),
    }),
    fetchScorDocuments: builder.query<
      ApiScorResponse<ApiScorDocumentsResponseData>, { language: string }>({
      providesTags: ['ScorDocuments'],
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/documents',
        method: 'GET',
        sendToken: true,
      }),
    }),
    fetchScorCategories: builder.query<
      ApiScorResponse<ApiScorCategoriesResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/categories',
        method: 'GET',
        sendToken: true,
      }),
    }),
    createScorRequest: builder.mutation<
      ApiScorResponse<ApiCreateScorResponseData>,
      ApiCreateScorRequestData
    >({
      query: (data) => {
        const { documents = [], uploadField, ...body } = data;

        return {
          url: '/v3/scor/scor/request',
          method: 'POST',
          sendToken: true,
          body: getScorFormData(body, documents, uploadField),
        };
      },
      invalidatesTags: ['ScorShowcase', 'ScorOrders', 'ScorInvoices', 'ScorDocuments'],
    }),
    fetchScorShareReports: builder.query<ApiScorResponse<ApiScorShareableResponseData>, { language: string }>({
      query: ({ language }) => ({
        url: '/v3/scor/scor/shareable',
        method: 'GET',
        sendToken: true,
        language,
      }),
      providesTags: ['ScorReports', 'ScorShares'],
    }),
    fetchScorMyReports: builder.query<
      ApiScorResponse<ApiScorMyReportsResponseData>,
      ApiScorPaginationQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/scor/my-reports',
        method: 'GET',
        sendToken: true,
        params,
      }),
      providesTags: ['ScorReports'],
    }),
    fetchScorMyOrders: builder.query<
      ApiScorResponse<ApiScorMyOrdersResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/my-orders',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorOrders'],
    }),
    openScorOrderChat: builder.mutation<
      ApiScorResponse<ApiOpenScorOrderChatResponseData>,
      ApiOpenScorOrderChatRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/scor/order-chat',
        method: 'POST',
        sendToken: true,
        body,
      }),
    }),
    uploadScorOrderDocuments: builder.mutation<
      ApiScorResponse<ApiUploadScorOrderDocumentsResponseData>,
      ApiUploadScorOrderDocumentsRequestData
    >({
      query: (data) => {
        const { documents, uploadField, ...body } = data;

        return {
          url: '/v3/scor/scor/order-documents',
          method: 'POST',
          sendToken: true,
          body: getScorFormData(body, documents, uploadField),
        };
      },
      invalidatesTags: ['ScorOrders', 'ScorShowcase'],
    }),
    fetchScorReportDetail: builder.query<
      ApiScorResponse<ApiScorReportDetailResponseData>,
      ApiScorReportDetailQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/scor/report-detail',
        method: 'GET',
        sendToken: true,
        params,
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(scorApi.util.invalidateTags(['ScorOrders', 'ScorReports', 'ScorShowcase']));
        } catch {
          // Failed opening does not change delivery state.
        }
      },
    }),
    fetchScorPool: builder.query<
      ApiScorResponse<ApiScorPoolResponseData>,
      ApiScorPoolQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/scor/pool',
        method: 'GET',
        sendToken: true,
        params,
      }),
    }),
    fetchScorPoolDetail: builder.query<
      ApiScorResponse<ApiScorPoolDetailResponseData>,
      ApiScorReportDetailQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/scor/pool-detail',
        method: 'GET',
        sendToken: true,
        params,
      }),
    }),
    findScorByCode: builder.query<
      ApiScorResponse<ApiFindScorByCodeResponseData>,
      ApiFindScorByCodeQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/scor/find-by-code',
        method: 'GET',
        sendToken: true,
        params,
      }),
    }),
    fetchScorShares: builder.query<
      ApiScorResponse<ApiScorSharesResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/shares',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorShares'],
    }),
    createScorShare: builder.mutation<
      ApiScorResponse<ApiCreateScorShareResponseData>,
      ApiCreateScorShareRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/scor/share-create',
        method: 'POST',
        sendToken: true,
        body,
      }),
      invalidatesTags: ['ScorShares'],
    }),
    revokeScorShare: builder.mutation<
      void,
      ApiRevokeScorShareRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/scor/share-revoke',
        method: 'POST',
        sendToken: true,
        body,
      }),
      invalidatesTags: ['ScorShares'],
    }),
    fetchScorSharedWithMe: builder.query<
      ApiScorResponse<ApiScorSharedWithMeResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/shared-with-me',
        method: 'GET',
        sendToken: true,
      }),
    }),
    fetchScorReferral: builder.query<
      ApiScorResponse<ApiScorReferral>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/referral',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorReferral'],
    }),
    applyScorReferral: builder.mutation<
      ApiScorResponse<ApiApplyScorReferralResponseData>,
      ApiApplyScorReferralRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/scor/referral-apply',
        method: 'POST',
        sendToken: true,
        body,
      }),
      invalidatesTags: ['ScorReferral'],
    }),
    fetchSharedScorReport: builder.query<
      ApiScorResponse<ApiFetchSharedScorReportResponseData>,
      ApiFetchSharedScorReportQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/shared/view',
        method: 'GET',
        sendToken: true,
        params,
      }),
    }),
    createScorPayment: builder.mutation<
      ApiScorResponse<ApiCreateScorPaymentResponseData>,
      ApiCreateScorPaymentRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/payment/create',
        method: 'POST',
        sendToken: true,
        body,
      }),
      invalidatesTags: ['ScorSubscription', 'ScorInvoices'],
    }),
    fetchScorSubscription: builder.query<
      ApiScorResponse<ApiScorSubscriptionResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/subscription',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorSubscription'],
    }),
    fetchScorMyInvoices: builder.query<
      ApiScorResponse<ApiScorMyInvoicesResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/my-invoices',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorInvoices'],
    }),
    fetchScorMyPurchases: builder.query<
      ApiScorResponse<ApiScorMyPurchasesResponseData>, { language: string }>({
      query: ({ language }) => ({
        language,
        url: '/v3/scor/scor/my-purchases',
        method: 'GET',
        sendToken: true,
      }),
      providesTags: ['ScorInvoices'],
    }),
    fetchScorInvoice: builder.query<
      ApiScorResponse<ApiScorInvoiceResponseData>,
      ApiFetchScorInvoiceQueryParams & { language: string }
    >({
      query: ({ language, ...params }) => ({
        language,
        url: '/v3/scor/scor/invoice',
        method: 'GET',
        sendToken: true,
        params,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'ScorInvoice', id },
      ],
    }),
    saveScorPayer: builder.mutation<
      ApiScorResponse<null>,
      ApiSaveScorPayerRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/scor/payer-save',
        method: 'POST',
        sendToken: true,
        body,
      }),
      invalidatesTags: (_result, _error, { invoice_id: id }) => [
        'ScorInvoices',
        { type: 'ScorInvoice', id },
      ],
    }),
    createScorSubscription: builder.mutation<
      ApiScorResponse<ApiCreateScorSubscriptionResponseData>,
      ApiCreateScorSubscriptionRequestData
    >({
      query: (body) => ({
        url: '/v3/scor/scor/subscription-request',
        method: 'POST',
        sendToken: true,
        body,
      }),
      invalidatesTags: ['ScorSubscription', 'ScorShowcase', 'ScorInvoices', 'ScorDocuments'],
    }),
  }),
});

export const {
  useFetchScorShareReportsQuery: useFetchScorShareReportsLocalizedQuery,
  useFetchScorSharedPreviewQuery: useFetchScorSharedPreviewLocalizedQuery,
  useMarkScorFaqViewedMutation,
  useFetchScorShowcaseQuery: useFetchScorShowcaseLocalizedQuery,
  useFetchScorPricingQuery: useFetchScorPricingLocalizedQuery,
  useFetchScorFaqQuery: useFetchScorFaqLocalizedQuery,
  useFetchScorDocumentsQuery: useFetchScorDocumentsLocalizedQuery,
  useFetchScorCategoriesQuery: useFetchScorCategoriesLocalizedQuery,
  useCreateScorRequestMutation,
  useFetchScorMyReportsQuery: useFetchScorMyReportsLocalizedQuery,
  useFetchScorMyOrdersQuery: useFetchScorMyOrdersLocalizedQuery,
  useOpenScorOrderChatMutation,
  useUploadScorOrderDocumentsMutation,
  useFetchScorReportDetailQuery: useFetchScorReportDetailLocalizedQuery,
  useFetchScorPoolQuery: useFetchScorPoolLocalizedQuery,
  useFetchScorPoolDetailQuery: useFetchScorPoolDetailLocalizedQuery,
  useFindScorByCodeQuery: useFindScorByCodeLocalizedQuery,
  useLazyFindScorByCodeQuery,
  useFetchScorSharesQuery: useFetchScorSharesLocalizedQuery,
  useCreateScorShareMutation,
  useRevokeScorShareMutation,
  useFetchScorSharedWithMeQuery: useFetchScorSharedWithMeLocalizedQuery,
  useFetchScorReferralQuery: useFetchScorReferralLocalizedQuery,
  useApplyScorReferralMutation,
  useFetchSharedScorReportQuery: useFetchSharedScorReportLocalizedQuery,
  useCreateScorPaymentMutation,
  useFetchScorSubscriptionQuery: useFetchScorSubscriptionLocalizedQuery,
  useFetchScorMyInvoicesQuery: useFetchScorMyInvoicesLocalizedQuery,
  useFetchScorMyPurchasesQuery: useFetchScorMyPurchasesLocalizedQuery,
  useFetchScorInvoiceQuery: useFetchScorInvoiceLocalizedQuery,
  useSaveScorPayerMutation,
  useCreateScorSubscriptionMutation,
} = scorApi;

export const useFetchScorShowcaseQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorShowcaseLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorShowcase.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorShowcaseLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorPricingQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorPricingLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorPricing.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorPricingLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorFaqQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorFaqLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorFaq.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorFaqLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorDocumentsQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorDocumentsLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorDocuments.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorDocumentsLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorCategoriesQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorCategoriesLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorCategories.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorCategoriesLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorMyReportsQuery = (params?: ApiScorPaginationQueryParams, options?: Omit<NonNullable<Parameters<typeof useFetchScorMyReportsLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorMyReports.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorMyReportsLocalizedQuery({ ...params, language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorMyOrdersQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorMyOrdersLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorMyOrders.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorMyOrdersLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorPoolQuery = (params?: ApiScorPoolQueryParams, options?: Omit<NonNullable<Parameters<typeof useFetchScorPoolLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorPool.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorPoolLocalizedQuery({ ...params, language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorSharesQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorSharesLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorShares.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorSharesLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorSharedWithMeQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorSharedWithMeLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorSharedWithMe.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorSharedWithMeLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorReferralQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorReferralLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorReferral.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorReferralLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorSubscriptionQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorSubscriptionLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorSubscription.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const language = getScorLanguage();
  const result = useFetchScorSubscriptionLocalizedQuery({ language }, options);
  const initial = useRef({ language, requestId: result.isFetching ? undefined : result.requestId });
  if (initial.current.language !== language) initial.current = { language, requestId: result.isFetching ? undefined : result.requestId };
  // A cached success can be returned before the mount-triggered refetch starts.
  const awaitingRefresh = options?.refetchOnMountOrArgChange === true && !options.skip && !result.isError &&
    initial.current.requestId !== undefined && result.requestId === initial.current.requestId;

  return { ...result, data: result.currentData, isLoading: awaitingRefresh || result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorMyInvoicesQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorMyInvoicesLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorMyInvoices.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorMyInvoicesLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorMyPurchasesQuery = (params?: void, options?: Omit<NonNullable<Parameters<typeof useFetchScorMyPurchasesLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorMyPurchases.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorMyPurchasesLocalizedQuery({ language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorInvoiceQuery = (params: ApiFetchScorInvoiceQueryParams, options?: Omit<NonNullable<Parameters<typeof useFetchScorInvoiceLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorInvoice.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorInvoiceLocalizedQuery({ ...params, language: getScorLanguage() }, options);

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorReportDetailQuery = (params: ApiScorReportDetailQueryParams, options?: Omit<NonNullable<Parameters<typeof useFetchScorReportDetailLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorReportDetail.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorReportDetailLocalizedQuery({ ...params, language: getScorLanguage() }, {
    ...options, refetchOnFocus: false, refetchOnReconnect: false,
  });

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorPoolDetailQuery = (params: ApiScorReportDetailQueryParams, options?: Omit<NonNullable<Parameters<typeof useFetchScorPoolDetailLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorPoolDetail.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorPoolDetailLocalizedQuery({ ...params, language: getScorLanguage() }, {
    ...options, refetchOnFocus: false, refetchOnReconnect: false,
  });

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchSharedScorReportQuery = (params: ApiFetchSharedScorReportQueryParams, options?: Omit<NonNullable<Parameters<typeof useFetchSharedScorReportLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchSharedScorReport.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchSharedScorReportLocalizedQuery({ ...params, language: getScorLanguage() }, {
    ...options, refetchOnFocus: false, refetchOnReconnect: false,
  });

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFindScorByCodeQuery = (params: ApiFindScorByCodeQueryParams, options?: Omit<NonNullable<Parameters<typeof useFindScorByCodeLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.findScorByCode.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFindScorByCodeLocalizedQuery({ ...params, language: getScorLanguage() }, {
    ...options, refetchOnFocus: false, refetchOnReconnect: false,
  });

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorSharedPreviewQuery = (params: { token: string }, options?: Omit<NonNullable<Parameters<typeof useFetchScorSharedPreviewLocalizedQuery>[1]>, 'selectFromResult'>): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorSharedPreview.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorSharedPreviewLocalizedQuery({ ...params, language: getScorLanguage() }, {
    ...options, refetchOnFocus: false, refetchOnReconnect: false,
  });

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};

export const useFetchScorShareReportsQuery = (): Omit<UseQueryHookResult<typeof scorApi.endpoints.fetchScorShareReports.Types.QueryDefinition>, 'isLoading'> & { isLoading: boolean } => {
  useTranslation();
  const result = useFetchScorShareReportsLocalizedQuery({ language: getScorLanguage() });

  return { ...result, data: result.currentData, isLoading: result.isLoading || (result.isFetching && !result.currentData) };
};
